import styled, { css } from 'styled-components';
import { ICON_SIZES } from '../../../../icon';
import { NAVIGATION_HEIGHT } from '../../../../navigation/constants/navigation-css-variables';
import Typography from '../../../../typography';
import { stylePropertiesToCss } from '../../../../../design-tokens/style-mixins';
import { getIconUrl, pxToRem } from '../../../../../design-tokens/style-utilities';
import { NAVIGATION_MAX_WIDTH, NAVIGATION_VIEWPORT_BREAKPOINTS } from '../../../constants';
import { NavigationFlyoutAsideElement } from '../../NavigationFlyoutAside/styles/navigation-flyout-aside.styled';
import { NavigationFlyoutRowElement } from '../../NavigationFlyoutRow/styles/navigation-flyout-row.styled';
const SHOW_ALL_OFFSET_TOP = 6;
const FLYOUT_SPACING_LG = 96;
const FLYOUT_SPACING_XL = 120;
const FLYOUT_SPACING_XXL = 123;
/**
 * Navigation flyout container
 */
export const navigationFlyoutContainerStyles = ({ theme, $isActive, }) => css `
	display: ${$isActive ? 'block' : 'none'};

	@media (min-width: ${pxToRem(NAVIGATION_VIEWPORT_BREAKPOINTS.lg)}) {
		position: absolute;
		z-index: ${theme.zIndex.flyOut};
		inset: var(${NAVIGATION_HEIGHT}) 0 0 0;

		/* Overlay for flyout */
		&::after {
			z-index: -1;
			content: '';
			position: absolute;
			inset: 0;
			background-color: ${theme.colors.base.overlayNavigation};
			pointer-events: none;
		}
	}

	/*
		Lingering portals, even when navigation is hidden for print mode, cause them to still take up height and therefore cause white pages.
		Putting every element in the navigation to display: none; fixes this.
	*/
	@media print {
		* {
			display: none;
		}
	}
`;
export const NavigationFlyoutContainer = styled.div `
	${navigationFlyoutContainerStyles}
`;
/**
 * Navigation flyout inner
 */
export const navigationFlyoutInnerStyles = ({ theme }) => css `
	background-color: ${theme.colors.blanc.bgMainNavigation};
`;
export const NavigationFlyoutInner = styled.div `
	${navigationFlyoutInnerStyles}
`;
/**
 * Navigation flyout content
 */
export const navigationFlyoutContentStyles = ({ theme }) => css `
	display: grid;
	grid-template-areas:
		'content'
		'header'
		'aside';
	max-width: ${pxToRem(NAVIGATION_MAX_WIDTH)};
	margin: 0 auto;

	@media (min-width: ${pxToRem(NAVIGATION_VIEWPORT_BREAKPOINTS.lg)}) {
		grid-template-areas:
			'header .'
			'content aside';
		grid-template-columns: auto 1fr;
		grid-template-rows: auto 1fr;
		gap: ${pxToRem(theme.spacing['500'])} ${pxToRem(theme.spacing['400'])};
		padding: ${pxToRem(theme.spacing['450'])} 0;
		margin: 0 ${pxToRem(FLYOUT_SPACING_LG)};
	}

	@media (min-width: ${pxToRem(NAVIGATION_VIEWPORT_BREAKPOINTS.xl)}) {
		margin: 0 ${pxToRem(FLYOUT_SPACING_XL)};
	}

	@media (min-width: ${pxToRem(NAVIGATION_VIEWPORT_BREAKPOINTS.xxl)}) {
		margin: 0 auto;
		padding-left: ${pxToRem(FLYOUT_SPACING_XXL)};
		padding-right: ${pxToRem(FLYOUT_SPACING_XXL)};
	}

	${NavigationFlyoutRowElement} {
		grid-area: content;
	}

	${NavigationFlyoutAsideElement} {
		grid-area: aside;

		@media (max-width: ${pxToRem(NAVIGATION_VIEWPORT_BREAKPOINTS.lg)}) {
			padding-top: ${pxToRem(theme.spacing['400'])};
		}

		@media (min-width: ${pxToRem(NAVIGATION_VIEWPORT_BREAKPOINTS.lg)}) {
			display: flex;
		}
	}
`;
export const NavigationFlyoutContent = styled.div `
	${navigationFlyoutContentStyles}
`;
/**
 * Navigation flyout header
 */
export const navigationFlyoutHeaderStyles = ({ theme }) => css `
	grid-area: header;
	display: flex;
	flex-direction: row;
	gap: ${pxToRem(theme.spacing['450'])};
`;
export const NavigationFlyoutHeader = styled.div `
	${navigationFlyoutHeaderStyles}
`;
/**
 * Navigation flyout title
 */
export const navigationFlyoutTitleStyles = ({ theme }) => css `
	display: none;
	margin: 0;
	color: ${theme.colors.base.textTitles};

	&:active {
		color: ${theme.colors.action.textActive};
	}

	&:focus-visible {
		outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocus};
		outline-offset: ${theme.borderWidth.m}px;
	}

	@media (min-width: ${pxToRem(NAVIGATION_VIEWPORT_BREAKPOINTS.lg)}) {
		display: block;
	}
`;
export const NavigationFlyoutTitle = styled(Typography).attrs({
    variant: 'component-title',
}) `
	${navigationFlyoutTitleStyles}
`;
/**
 * Navigation flyout show all
 */
export const navigationFlyoutShowAllStyles = ({ theme }) => css `
	${stylePropertiesToCss(theme.typography.buttons['100ButtonLink'])};

	flex: 1;
	position: relative;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	padding: ${pxToRem(theme.spacing['200'])} ${pxToRem(theme.spacing['100'])};
	margin-top: ${pxToRem(theme.spacing['300'])};
	text-decoration: none;
	color: ${theme.colors.highlight.textInteractive};

	&::before {
		content: '';
		position: absolute;
		top: -${pxToRem(SHOW_ALL_OFFSET_TOP)};
		right: 0;
		left: 0;
		height: ${theme.borderWidth.m}px;
		background-color: ${theme.colors.base.borderActive};
	}

	&::after {
		content: '';
		mask: url('${getIconUrl('chevron-right', 'ui')}') no-repeat center right;
		background-color: ${theme.colors.highlight.iconInteractive};
		height: ${pxToRem(ICON_SIZES.lg)};
		width: ${pxToRem(ICON_SIZES.lg)};
	}

	&:hover {
		color: ${theme.colors.base.textNavigationHover};

		&::after {
			background-color: ${theme.colors.base.textNavigationHover};
		}
	}

	&:active,
	&:focus {
		color: ${theme.colors.action.textActive};

		&::after {
			background-color: ${theme.colors.action.textActive};
		}
	}

	&:focus-visible {
		outline: ${theme.borderWidth.m}px solid ${theme.colors.highlight.borderFocus};
		outline-offset: ${theme.borderWidth.m}px;
	}

	@media (max-width: ${pxToRem(Number(NAVIGATION_VIEWPORT_BREAKPOINTS.lg) - 1)}) {
		border-radius: ${pxToRem(theme.borderRadius.xs)};

		&:hover {
			color: ${theme.colors.highlight.textHover};
			background-color: ${theme.colors.extralight.bgNavigationItemHover};

			&::after {
				background-color: ${theme.colors.highlight.iconHover};
			}
		}

		&:active {
			background-color: ${theme.colors.base.bgNavigationItemActive};
			color: ${theme.colors.blanc.textInverse};

			&::after {
				background-color: ${theme.colors.blanc.iconInteractiveInverse};
			}
		}
	}

	@media (min-width: ${pxToRem(NAVIGATION_VIEWPORT_BREAKPOINTS.lg)}) {
		${stylePropertiesToCss(theme.typography.heading4['300ComponentTitleSlimXs'])};
		color: ${theme.colors.base.textTitles};
		flex: unset;
		display: inline-flex;
		justify-content: flex-start;
		gap: ${pxToRem(theme.spacing['200'])};
		padding: 0;
		margin-top: 0;

		&::before {
			display: none;
		}
	}
`;
export const NavigationFlyoutShowAll = styled(Typography).attrs({
    tagName: 'a',
}) `
	${navigationFlyoutShowAllStyles}
`;
